/**
 * Bulk tagging, the part with no database in it.
 *
 * Pure by design, the same way `filters.ts` is: the menu, the undo and the
 * tests all answer "which agents, and how many" with this code rather than
 * three versions of it. `tagwrites.ts` holds the two functions that touch
 * Supabase.
 *
 * Two rules shape the feature:
 *
 * 1. **The count is shown before anything is written** — not "tag these
 *    agents" but "tag 374 agents", the shape the export counts already use.
 * 2. **Every write is undoable in one click.** A tag applied to the wrong 386
 *    people must not need 386 clicks to take back, so each write reports
 *    exactly which agents it changed and the inverse runs over precisely those.
 */

export const MAX_TAG_LENGTH = 40

/**
 * Tags are normalised on the way in, and this matters more than it looks.
 *
 * `agent_tags` is keyed `(agent_id, tag)`, so "VIP" and "vip" are two rows, two
 * entries in the tag dropdown, and two different answers to "who is tagged
 * VIP". The filter would then be quietly wrong in a way nobody spots until a
 * campaign reaches half the intended list.
 *
 * The tags that already exist are lower-case and hyphenated (`no-outreach`,
 * `no-birthday-email`) and `outreach-triggers.js` lower-cases before comparing,
 * so this matches what is already true rather than inventing a convention.
 *
 * **Normalising on write does not mean every stored row is normalised.** Rows
 * predating this file, or typed into the Supabase table editor, can be stored
 * any way at all — which is why `storedVariants` exists and why removal never
 * assumes the stored string equals the normalised one.
 */
export function normaliseTag(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, ' ')
}

/**
 * What is wrong with this tag, in words Renee can act on — or null if nothing
 * is. First problem only; a list of complaints about one text box is worse than
 * one sentence.
 */
export function tagProblem(raw: string): string | null {
  const tag = normaliseTag(raw)
  if (!tag) return 'Type a tag first.'
  if (tag.length > MAX_TAG_LENGTH) {
    // "Keep it under 40" would be wrong: 40 is allowed. Someone at exactly the
    // limit who follows that instruction deletes a character they did not have
    // to, and the number in the sentence is the one they can see in the box.
    return `That tag is ${tag.length} characters. The most it can be is ${MAX_TAG_LENGTH}.`
  }
  if (tag.includes(',')) return 'Tags cannot contain a comma — add them one at a time.'
  return null
}

/**
 * Tags the outreach system acts on rather than tags Renee invented. Applying
 * one of these in bulk changes who gets email, so the menu asks twice.
 *
 * `no-bulk-email` is planned rather than live — see `claude/email-lane-specs.md`
 * — and is listed here so it is guarded from the day it exists rather than the
 * day after someone applies it to 785 people.
 */
export const SYSTEM_TAGS: readonly string[] = [
  'no-outreach',
  'no-bulk-email',
  'no-birthday-email',
  'no-anniversary-email',
  'no-capped-email',
  'no-elite-email',
  'no-joined-email',
  'no-firstTransaction-email',
]

export function isSystemTag(raw: string): boolean {
  const tag = normaliseTag(raw)
  // The per-trigger tags follow a shape rather than a list, so match the shape
  // too — a future trigger gets guarded without this file changing.
  return SYSTEM_TAGS.includes(tag) || /^no-[a-z-]+-email$/.test(tag) || tag === 'no-outreach'
}

/**
 * Above this many agents, a write asks for confirmation before it runs.
 *
 * Not a limit — she can still do it, and there are real reasons to tag the
 * whole roster. It is the point where "I meant to do that" stops being obvious
 * from the button alone.
 */
export const CONFIRM_ABOVE = 25

export function needsConfirming(count: number, rawTag: string): boolean {
  return count > CONFIRM_ABOVE || isSystemTag(rawTag)
}

/**
 * PostgREST puts `in` lists in the query string, so a delete covering the whole
 * roster would build a URL around 29,000 characters from 785 UUIDs. That fails
 * at the proxy rather than in our code, and it fails *only* at scale — a test
 * with five agents passes happily. Chunking keeps every request small and lets
 * a partial failure report which agents actually changed.
 */
export const CHUNK_SIZE = 100

export function chunk<T>(items: readonly T[], size: number = CHUNK_SIZE): T[][] {
  if (!Number.isInteger(size) || size < 1) throw new Error('chunk size must be a positive integer')
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

export type TagAction = 'add' | 'remove'

export type TagOutcome = {
  action: TagAction
  /** The tag after normalisation — what was actually written. */
  tag: string
  /** Agents this operation was asked to change. */
  attempted: number
  /**
   * Agents it actually changed, **observed from the database rather than
   * assumed from what was sent.** An undo acts on exactly these, so an
   * over-estimate here means an undo deleting rows a human created.
   */
  changed: string[]
  /**
   * Set when something went wrong. Never "it failed" on a partial run — that is
   * untrue and unactionable, and the difference between 0 and 340 written rows
   * is the entire question.
   */
  error: string | null
}

export function readableWriteError(message: string): string {
  const m = message.toLowerCase()
  if (m.includes('permission denied') || m.includes('jwt') || m.includes('not authorized')) {
    return 'Your sign-in expired. Refresh the page and sign in again.'
  }
  return message
}

/**
 * How a partial run is described.
 *
 * Direction matters: on a removal the agents that "changed" are the ones the
 * tag came *off*, so a single hard-coded sentence about things being "still
 * applied" is exactly backwards half the time — and the two readings differ by
 * the whole selection.
 *
 * It also must not contradict itself. `readableWriteError` used to end with
 * "nothing was changed", which sat directly under a sentence saying 340 of 386
 * had — reachable any time a token expires partway through eight sequential
 * round trips.
 */
export function partialMessage(
  action: TagAction,
  changed: number,
  attempted: number,
  message: string,
): string {
  const why = readableWriteError(message)
  if (changed === 0) return `Nothing was changed. ${why}`
  const left = attempted - changed
  return action === 'add'
    ? `${changed} of ${attempted} were tagged before this stopped — those ${changed === 1 ? 'is' : 'are'} tagged now, ${left} ${left === 1 ? 'is' : 'are'} not. ${why}`
    : `The tag came off ${changed} of ${attempted} before this stopped — ${left} still ${left === 1 ? 'has' : 'have'} it. ${why}`
}

/**
 * Split a selection by whether each agent already carries the tag.
 *
 * This is what puts both numbers on screen before anything is written, and it
 * is why the button can say "Tag 374" rather than "Tag 386" when twelve of them
 * already have it.
 *
 * **An empty tag matches nothing in either direction.** Returning the whole
 * roster as "not tagged" put "Tag 786 agents" on the biggest button in the
 * menu before Renee had typed a character — disabled, but the number is the
 * thing the eye lands on.
 */
export function splitByTag(
  agents: readonly { rezen_id: string }[],
  tagsByAgent: Map<string, string[]>,
  rawTag: string,
): { withTag: string[]; withoutTag: string[] } {
  const tag = normaliseTag(rawTag)
  const withTag: string[] = []
  const withoutTag: string[] = []
  if (!tag) return { withTag, withoutTag }
  for (const a of agents) {
    const has = (tagsByAgent.get(a.rezen_id) ?? []).some((t) => normaliseTag(t) === tag)
    ;(has ? withTag : withoutTag).push(a.rezen_id)
  }
  return { withTag, withoutTag }
}

/**
 * Every distinct way this tag is actually spelled in storage.
 *
 * A delete matching `tag = 'vip'` exactly will not touch a row stored as
 * 'VIP' — while `splitByTag`, which normalises both sides, counted that agent
 * as having it. The button would say "Remove from 13", seven rows would go, and
 * the outcome would report a clean success. Removal therefore deletes these
 * strings, not the normalised one.
 */
export function storedVariants(tagsByAgent: Map<string, string[]>, rawTag: string): string[] {
  const tag = normaliseTag(rawTag)
  const seen = new Set<string>()
  if (!tag) return []
  for (const list of tagsByAgent.values()) {
    for (const t of list) if (normaliseTag(t) === tag) seen.add(t)
  }
  // The normalised form is always included: it is what a fresh write stores,
  // and it may not appear in a map that is a moment out of date.
  seen.add(tag)
  return [...seen].sort()
}

/**
 * Every tag in use, normalised and deduplicated, alphabetical — so the menu can
 * offer what exists rather than making her remember how she spelled it last
 * time. Re-typing an existing tag slightly differently is the likeliest way to
 * end up with two tags that should have been one.
 */
export function existingTags(tagsByAgent: Map<string, string[]>): string[] {
  const seen = new Set<string>()
  for (const list of tagsByAgent.values()) {
    for (const t of list) {
      const n = normaliseTag(t)
      if (n) seen.add(n)
    }
  }
  return [...seen].sort((a, b) => a.localeCompare(b))
}

/**
 * Merge a fresh outcome into the one the Undo button is holding.
 *
 * Retrying after a partial failure produced two writes and an Undo covering
 * only the second: 300 tagged, then 86 tagged, then "Undo" took the tag off 86
 * and left 300 stranded. Same tag and same direction means one job, so the
 * undo has to cover both halves of it.
 */
export function mergeOutcomes(prev: TagOutcome | null, next: TagOutcome): TagOutcome {
  if (!prev || prev.action !== next.action || prev.tag !== next.tag) return next
  const changed = [...new Set([...prev.changed, ...next.changed])]
  return {
    ...next,
    attempted: prev.attempted + next.attempted,
    changed,
  }
}

/**
 * Given the agents currently holding a tag — read from the database, not from
 * a client-side map that may be an hour old — which of a selection need it
 * written, and which need it removed.
 *
 * Trivial arithmetic, extracted so it is testable without a database. It is
 * also where the most dangerous bug in this feature lived: assuming "the ones I
 * sent" equals "the ones that changed" let an undo of a no-op write delete 386
 * rows a human had created.
 */
export function needingTag(agentIds: readonly string[], holders: ReadonlySet<string>): string[] {
  return agentIds.filter((id) => !holders.has(id))
}

export function holdingTag(agentIds: readonly string[], holders: ReadonlySet<string>): string[] {
  return agentIds.filter((id) => holders.has(id))
}
